import {
  Box, Container, Typography, Grid, Card, CardContent, CardMedia,
  CardActionArea, Chip, Pagination, CircularProgress,
} from "@mui/material";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { useGetPublishedPostsQuery, useGetPostsByCategoryQuery, useGetPublicCategoriesQuery, useGetWebsiteSettingsQuery } from "../../../../../app/websiteApiSlice";
import { useGetPublicSchoolInfoQuery } from "../../../../../app/authApiSlice";
import WebsiteHeader from "../../WebsiteHeader";
import WebsiteFooter from "../../WebsiteFooter";
import SeoHead from "../../SeoHead";
import "./modern.css";

export default function ModernPostsList({ settings: settingsProp, categorySlug, page, onPageChange }) {
  const { data: school } = useGetPublicSchoolInfoQuery();
  const { data: settings } = useGetWebsiteSettingsQuery(undefined, { skip: !!settingsProp });
  const activeSettings = settingsProp || settings;
  const { data: categories = [] } = useGetPublicCategoriesQuery();

  const { data: allPosts, isLoading: loadingAll } = useGetPublishedPostsQuery(
    { page, per_page: 9 },
    { skip: !!categorySlug }
  );
  const { data: catData, isLoading: loadingCat } = useGetPostsByCategoryQuery(
    { slug: categorySlug, page, per_page: 9 },
    { skip: !categorySlug }
  );

  const isLoading = categorySlug ? loadingCat : loadingAll;
  const data = categorySlug ? catData : allPosts;
  const posts = data?.posts || [];
  const totalPages = data?.pages || 1;
  const activeCategory = categories.find((c) => c.slug === categorySlug);

  return (
    <Box className="website-modern" sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <SeoHead
        title={activeCategory ? `${activeCategory.name} — Blog` : "Blog"}
        schoolName={school?.name}
      />
      <WebsiteHeader school={school} navItems={activeSettings?.nav_items} variant="modern" />

      <Container maxWidth="lg" sx={{ py: 6, flex: 1 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          {activeCategory ? activeCategory.name : "Blog"}
        </Typography>

        {/* Category filter */}
        {categories.length > 0 && (
          <Box display="flex" gap={1} flexWrap="wrap" mb={4}>
            <Chip
              label="All"
              component={Link}
              to="/blog"
              clickable
              color={!categorySlug ? "primary" : "default"}
              variant={!categorySlug ? "filled" : "outlined"}
            />
            {categories.map((c) => (
              <Chip
                key={c.id}
                label={c.name}
                component={Link}
                to={`/blog/category/${c.slug}`}
                clickable
                color={categorySlug === c.slug ? "primary" : "default"}
                variant={categorySlug === c.slug ? "filled" : "outlined"}
              />
            ))}
          </Box>
        )}

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : posts.length === 0 ? (
          <Typography variant="body1" color="text.secondary" py={6} textAlign="center">
            No posts yet.
          </Typography>
        ) : (
          <>
            <Grid container spacing={3} mb={4}>
              {posts.map((post) => (
                <Grid item xs={12} sm={6} md={4} key={post.id}>
                  <Card variant="outlined" sx={{ height: "100%", borderRadius: 3, display: "flex", flexDirection: "column", "&:hover": { boxShadow: 3 } }}>
                    <CardActionArea component={Link} to={`/blog/${post.slug}`} sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "stretch" }}>
                      {post.featured_image && (
                        <CardMedia component="img" height="160" image={post.featured_image} alt={post.title} />
                      )}
                      <CardContent sx={{ flex: 1 }}>
                        <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
                          {post.categories?.map((c) => (
                            <Chip key={c.id} label={c.name} size="small" variant="outlined" color="primary" />
                          ))}
                        </Box>
                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>{post.title}</Typography>
                        {post.excerpt && (
                          <Typography variant="body2" color="text.secondary" sx={{
                            display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 3, overflow: "hidden"
                          }}>
                            {post.excerpt}
                          </Typography>
                        )}
                      </CardContent>
                      {post.published_at && (
                        <Box px={2} pb={2}>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(post.published_at), "MMM d, yyyy")}
                          </Typography>
                        </Box>
                      )}
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
            {totalPages > 1 && (
              <Box display="flex" justifyContent="center">
                <Pagination count={totalPages} page={page} onChange={(_e, v) => onPageChange(v)} color="primary" />
              </Box>
            )}
          </>
        )}
      </Container>

      <WebsiteFooter settings={activeSettings} school={school} variant="modern" />
    </Box>
  );
}
